<template>
  <div class="billing-table">
    <!-- Header Actions -->
    <div class="header-actions mb-4">
      <div class="flex flex-wrap justify-between items-center gap-4">
        <div class="flex items-center gap-3">
          <vs-button
            v-if="countSelected > 0"
            @click="openPopupChangeState"
            v-can="'use_accounting_panel_billing'"
            icon-pack="feather"
            icon="icon-edit"
            color="primary"
          >
            <span class="ml-2">Cambiar estado ({{ countSelected }})</span>
          </vs-button>
        </div>
        <div>
          <vs-button
            type="border"
            color="success"
            :icon="isExporting ? 'refresh' : 'file_download'"
            :disabled="isExporting"
            @click="exportExcel"
          >{{ isExporting ? 'Exportando...' : 'Exportar tabla a Excel' }}</vs-button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex flex-col items-center justify-center py-16 gap-4">
      <div class="spinner"></div>
      <p class="text-base font-medium text-grey-dark">Cargando datos...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!isLoading && rowData.length === 0" class="flex flex-col items-center justify-center py-20 gap-4 rounded-lg d-theme-dark-bg p-6">
      <feather-icon icon="FileTextIcon" svgClasses="w-16 h-16 text-primary opacity-50" />
      <p class="text-xl font-bold text-dark">Sin registros</p>
      <p class="text-sm text-grey">No hay facturaciones que mostrar</p>
    </div>

    <!-- Table Container -->
    <vx-card v-else class="table-wrapper-card">
      <!-- Scrollable wrapper for table + pagination -->
      <div class="table-scroll-wrapper">
        <!-- Desktop Table -->
        <table class="closure-data-table">
        <thead>
          <tr>
            <th class="col-checkbox"><input type="checkbox" v-model="selectAll" @change="toggleSelectAll" class="cursor-pointer"></th>
            <th class="col-campaign sortable" @click="toggleSort('campaign_id')">
              <span class="sortable-header">
                ID Campaña
                <span class="sort-icons">
                  <feather-icon icon="ChevronUpIcon" :svgClasses="sortChevronClass('campaign_id', 'asc')" />
                  <feather-icon icon="ChevronDownIcon" :svgClasses="sortChevronClass('campaign_id', 'desc')" />
                </span>
              </span>
            </th>
            <th class="col-id sortable" @click="toggleSort('closure_number')">
              <span class="sortable-header">
                Número cierre
                <span class="sort-icons">
                  <feather-icon icon="ChevronUpIcon" :svgClasses="sortChevronClass('closure_number', 'asc')" />
                  <feather-icon icon="ChevronDownIcon" :svgClasses="sortChevronClass('closure_number', 'desc')" />
                </span>
              </span>
            </th>
            <th class="col-campaign-name sortable" @click="toggleSort('campaign_name')">
              <span class="sortable-header">
                Nombre Campaña
                <span class="sort-icons">
                  <feather-icon icon="ChevronUpIcon" :svgClasses="sortChevronClass('campaign_name', 'asc')" />
                  <feather-icon icon="ChevronDownIcon" :svgClasses="sortChevronClass('campaign_name', 'desc')" />
                </span>
              </span>
            </th>
            <th class="col-agency sortable" @click="toggleSort('agency')">
              <span class="sortable-header">
                Agencia
                <span class="sort-icons">
                  <feather-icon icon="ChevronUpIcon" :svgClasses="sortChevronClass('agency', 'asc')" />
                  <feather-icon icon="ChevronDownIcon" :svgClasses="sortChevronClass('agency', 'desc')" />
                </span>
              </span>
            </th>
            <th class="col-holding sortable" @click="toggleSort('holding')">
              <span class="sortable-header">
                Holding
                <span class="sort-icons">
                  <feather-icon icon="ChevronUpIcon" :svgClasses="sortChevronClass('holding', 'asc')" />
                  <feather-icon icon="ChevronDownIcon" :svgClasses="sortChevronClass('holding', 'desc')" />
                </span>
              </span>
            </th>
            <th class="col-legal-name sortable" @click="toggleSort('billing_information')">
              <span class="sortable-header">
                Razón Social
                <span class="sort-icons">
                  <feather-icon icon="ChevronUpIcon" :svgClasses="sortChevronClass('billing_information', 'asc')" />
                  <feather-icon icon="ChevronDownIcon" :svgClasses="sortChevronClass('billing_information', 'desc')" />
                </span>
              </span>
            </th>
            <th class="col-comment">Comentario</th>
            <th class="col-period">Período</th>
            <th class="col-amount">Total</th>
            <th class="col-currency">Moneda</th>
            <th class="col-due-date sortable" @click="toggleSort(DUE_DATE_SORT)">
              <span class="sortable-header">
                Vencimiento
                <span class="sort-icons">
                  <feather-icon icon="ChevronUpIcon" :svgClasses="sortChevronClass(DUE_DATE_SORT, 'asc')" />
                  <feather-icon icon="ChevronDownIcon" :svgClasses="sortChevronClass(DUE_DATE_SORT, 'desc')" />
                </span>
              </span>
            </th>
            <th class="col-status">Estado</th>
            <th class="col-actions">Ver +</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rowData" :key="index" :class="{ 'row-selected': isRowSelected(row) }">
            <td class="col-checkbox">
              <input
                type="checkbox"
                :checked="isRowSelected(row)"
                @change="toggleRowSelection(row)"
                class="cursor-pointer"
              >
            </td>
            <td class="col-campaign" data-label="ID Campaña">
              <span class="campaign-name">{{ row.campaign_id || '-' }}</span>
            </td>
            <td class="col-id" data-label="Número cierre">
              <span class="id-badge">{{ row.closure_number }}</span>
            </td>
            <td class="col-campaign-name" data-label="Nombre Campaña">
              <span v-if="(row.campaign_name || '').length <= 20" class="campaign-name">{{ row.campaign_name || '-' }}</span>
              <span v-else class="campaign-name">{{ (row.campaign_name || '').substring(0, 20) }}...</span>
            </td>
            <td class="col-agency" data-label="Agencia">
              <span class="agency-name">{{ (row.company || {}).name || '-' }}</span>
              <span v-if="row.responsible_agency" class="responsible-agency-name">{{ row.responsible_agency.name }}</span>
            </td>
            <td class="col-holding" data-label="Holding">
              <span class="holding-name">{{ row.holding || '-' }}</span>
            </td>
            <td class="col-legal-name" data-label="Razón Social">
              <span v-if="((row.billing_information || {}).name || '').length <= 20" class="legal-name">{{ (row.billing_information || {}).name || '-' }}</span>
              <span v-else class="legal-name">{{ ((row.billing_information || {}).name || '').substring(0, 20) }}...</span>
            </td>
            <td class="col-comment" data-label="Comentario">
              <div class="flex items-center gap-1">
                <span v-if="(row.campaign_comment || '').length <= 25" class="comment-text">{{ row.campaign_comment || '-' }}</span>
                <div v-else class="comment-preview">
                  <span class="comment-text">{{ (row.campaign_comment || '').substring(0, 25) }}...</span>
                  <vs-button
                    size="small"
                    type="border"
                    color="primary"
                    @click="openCommentModal(row.campaign_comment)"
                    class="comment-expand-btn"
                  >
                    Ver
                  </vs-button>
                </div>
                <feather-icon
                  v-if="row.billing_comment"
                  icon="FileTextIcon"
                  svgClasses="w-4 h-4 text-primary cursor-pointer flex-shrink-0"
                  title="Ver comentario de facturación"
                  @click.stop="openCommentModal(row.billing_comment, 'Comentario de facturación')"
                />
              </div>
            </td>
            <td class="col-period" data-label="Período">
              <span class="period">{{ formatPeriod(row.date_from, row.date_to) }}</span>
            </td>
            <td class="col-amount" data-label="Total a Facturar">
              <span class="amount">{{ row.total_to_bill != null ? parseFloat(row.total_to_bill).toFixed(2) : (row.amount ? parseFloat(row.amount).toFixed(2) : '0.00') }}</span>
            </td>
            <td class="col-currency" data-label="Moneda">
              <span class="currency">{{ row.currency || 'USD' }}</span>
            </td>
            <td class="col-due-date" data-label="Vencimiento">
              <span class="due-date">{{ formatDate(row.billing_due_date) || '-' }}</span>
            </td>            
            <td class="col-status" data-label="Estado">
              <div class="status-chip-wrapper" @click="changeStateForRow(row)">
                <vs-chip
                  :color="row.billing_status === 'billed' ? '#28C76F' : '#EA5455'"
                  textWhite
                  size="small"
                  class="status-chip"
                >
                  {{ getBillingStatusLabel(row) }}
                  <feather-icon icon="Edit2Icon" svgClasses="w-3 h-3 ml-1 opacity-75" />
                </vs-chip>
                <feather-icon
                  v-if="(row.billing || {}).cancellation_comment"
                  icon="InfoIcon"
                  svgClasses="w-4 h-4 text-danger cursor-pointer flex-shrink-0"
                  title="Ver motivo de anulación"
                  @click.stop="openCommentModal(row.billing.cancellation_comment, 'Motivo de anulación')"
                />
              </div>
            </td>
            <td class="col-actions">
              <vs-button
                size="small"
                type="border"
                color="primary"
                icon-pack="feather"
                icon="icon-plus"
                @click="openDetailModal(row)"
              />
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      <!-- Pagination Footer -->
      <div class="table-footer" v-if="rowData.length > 0">
        <div class="pagination-info">
          <span class="text-grey-dark text-sm">
            Mostrando {{ displayRangeStart }}-{{ displayRangeEnd }} de {{ effectiveTotal }} registros
          </span>
        </div>
        <div class="pagination-controls">
          <vs-button
            :disabled="currentPage <= 1"
            @click="currentPage = currentPage - 1"
            type="border"
            icon-pack="feather"
            icon="icon-chevron-left"
            size="small"
          />
          <div class="pagination-numbers">
            <vs-button
              v-for="page in pageRange"
              :key="page"
              @click="currentPage = page"
              :color="currentPage === page ? 'primary' : ''"
              size="small"
              class="page-button"
            >
              {{ page }}
            </vs-button>
          </div>
          <vs-button
            :disabled="currentPage >= effectivePages"
            @click="currentPage = currentPage + 1"
            type="border"
            icon-pack="feather"
            icon="icon-chevron-right"
            size="small"
          />
        </div>
      </div>
    </vx-card>

    <!-- Detail popup -->
    <billing-details
      :modalActive.sync="modalActive"
      :modalData="detailModalData"
    />

    <!-- Change state popup -->
    <billing-state-change-popup
      :popupActive.sync="popupActive"
      :selectedRows="selectedRows"
      @refresh="getGridData"
    />

    <!-- Comment Modal -->
    <comment-modal
      :modalActive.sync="commentModalActive"
      :comment="fullCommentText"
      :title="commentModalTitle"
    />
  </div>
</template>

<style lang="scss">
@import './css/billing-charges-tables.scss';
</style>

<style scoped>
.billing-table {
  width: 100%;
  padding: 0;
}
</style>

<script>
import moment from 'moment'
import qs from 'qs'
import appConfig from '@/../appConfig.js'
import BillingDetails from '../components/popups/billing-details/BillingDetails.vue'
import BillingStateChangePopup from '../components/popups/billing-state-popup/BillingStateChangePopup.vue'
import CommentModal from '../components/popups/comment-modal/CommentModal.vue'

const BILLING_STATUS_LABELS = {
  billed: 'Facturado',
  unbilled: 'No facturado'
}

// Due date is the backend default: it sorts by it when no sort_by is sent
const DUE_DATE_SORT = 'due_date'


export default {
  name: 'billing-table',
  components: {
    BillingDetails,
    BillingStateChangePopup,
    CommentModal
  },
  props: {
    filters: {
      type: Object,
      default: () => ({
        holdings: [],
        companies: [],
        dateRange: [null, null]
      })
    }
  },
  data() {
    return {
      currentPage: 1,
      totalRows: 0,
      totalPages: 1,
      rowData: [],
      selectedRows: [],
      selectAll: false,
      isLoading: false,
      modalActive: false,
      detailModalData: null,
      popupActive: false,
      commentModalActive: false,
      fullCommentText: '',
      commentModalTitle: 'Comentario completo',
      isExporting: false,
      sortBy: DUE_DATE_SORT,
      sortDir: null,
      DUE_DATE_SORT
    }
  },
  computed: {
    countSelected() {
      return this.selectedRows.length
    },
    effectiveTotal() {
      return this.totalRows > 0 ? this.totalRows : this.rowData.length
    },
    effectivePages() {
      return this.totalPages > 1 ? this.totalPages : 1
    },
    displayRangeStart() {
      return this.rowData.length > 0 ? (this.currentPage - 1) * 20 + 1 : 0
    },
    displayRangeEnd() {
      if (this.totalRows > 0) return Math.min(this.currentPage * 20, this.totalRows)
      return this.rowData.length
    },
    pageRange() {
      const totalPages = this.effectivePages
      const currentPage = this.currentPage
      const maxVisible = 5
      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
      let end = Math.min(totalPages, start + maxVisible - 1)
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1)
      }
      const range = []
      for (let i = start; i <= end; i++) {
        range.push(i)
      }
      return range
    }
  },
  watch: {
    currentPage(newPage) {
      if (newPage !== 1) {
        this.getGridData({ page: newPage })
      }
    }
  },
  async mounted() {
    // Auto-load data
    this.getGridData({ page: 1 })
  },
  methods: {
    forceSearch({ page = 1 } = {}) {
      this.isLoading = false
      this.getGridData({ page })
    },
    /**
     * Filters and sorting shared by the grid and the Excel export, so both stay in sync
     */
    buildRequestParams() {
      const { holdings, companies, dateRange, status, campaignId } = this.filters
      const holdingArray = holdings && holdings.length > 0 && holdings[0].value !== 'all'
        ? holdings.map(h => h.value)
        : null
      const companyArray = companies && companies.length > 0 && companies[0].value !== 'all'
        ? companies.map(c => c.value)
        : null

      const params = {}

      if (holdingArray) params.holding_id = holdingArray
      if (companyArray) params.company_id = companyArray
      if (dateRange && dateRange[0]) params.date_from = dateRange[0]
      if (dateRange && dateRange[1]) params.date_to = dateRange[1]
      if (status) params.status = status
      if (campaignId) params.campaign_id = campaignId

      if (this.sortDir) {
        params.order = this.sortDir
        // Due date is the backend default, it expects no sort_by for it
        if (this.sortBy !== DUE_DATE_SORT) params.sort_by = this.sortBy
      }

      return params
    },
    getGridData({ page = 1, pageSize = 20 } = {}) {
      if (this.isLoading) return
      this.isLoading = true

      const params = {
        ...this.buildRequestParams(),
        page,
        per_page: pageSize
      }

      this.$http
        .get(`${appConfig.apiUrl}/sales-closure-billings`, {
          params,
          paramsSerializer: p => qs.stringify(p, { arrayFormat: 'brackets' })
        })
        .then(response => {
          const responseData = response.data || {}
          const data = responseData.data || []
          const pagination = responseData.pagination || { current_page: 1, total: 0, last_page: 1 }

          this.rowData = data
          this.currentPage = pagination.current_page || 1
          this.totalRows = pagination.total || 0
          this.totalPages = pagination.last_page || 1
          this.selectedRows = []
          this.selectAll = false
        })
        .catch(error => {
          console.error('Error fetching billings:', error)
          this.$vs.notify({
            title: 'Error',
            text: 'Error al cargar datos de facturación',
            color: 'danger'
          })
          this.rowData = []
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    formatPeriod(dateFrom, dateTo) {
      const from = moment(dateFrom).format('DD/MM/YYYY')
      const to = moment(dateTo).format('DD/MM/YYYY')
      return `${from} - ${to}`
    },

    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date + 'T00:00:00Z')
      const day = String(d.getUTCDate()).padStart(2, '0')
      const month = String(d.getUTCMonth() + 1).padStart(2, '0')
      const year = d.getUTCFullYear()
      return `${day}/${month}/${year}`
    },

    getBillingStatusLabel(row) {
      return BILLING_STATUS_LABELS[row.billing_status] || 'Desconocido'
    },

    isRowSelected(row) {
      return this.selectedRows.some(r => r.id === row.id)
    },

    toggleRowSelection(row) {
      const index = this.selectedRows.findIndex(r => r.id === row.id)
      if (index > -1) {
        this.selectedRows.splice(index, 1)
      } else {
        this.selectedRows.push(row)
      }
      this.updateSelectAllCheckbox()
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedRows = [...this.rowData]
      } else {
        this.selectedRows = []
      }
    },

    updateSelectAllCheckbox() {
      this.selectAll = this.selectedRows.length === this.rowData.length
    },

    openDetailModal(data) {
      this.detailModalData = data
      this.modalActive = true
    },

    openPopupChangeState() {
      if (this.selectedRows.length > 0) {
        this.popupActive = true
      } else {
        this.$vs.notify({
          title: 'Atención',
          text: 'Selecciona al menos una fila',
          color: 'warning'
        })
      }
    },

    changeStateForRow(row) {
      this.selectedRows = [row]
      this.popupActive = true
    },

    toggleSort(column) {
      if (this.sortBy !== column) {
        this.sortBy = column
        this.sortDir = 'asc'
      } else if (this.sortDir === null) {
        this.sortDir = 'asc'
      } else if (this.sortDir === 'asc') {
        this.sortDir = 'desc'
      } else {
        // Third click clears the sort and falls back to the backend default
        this.sortDir = null
        this.sortBy = DUE_DATE_SORT
      }

      this.getGridData({ page: 1 })
    },

    sortChevronClass(column, direction) {
      const isActive = this.sortBy === column && this.sortDir === direction

      return 'sort-chevron' + (isActive ? ' sort-active' : '')
    },

    async exportExcel() {
      this.isExporting = true

      try {
        const response = await this.$http.get(`${appConfig.apiUrl}/sales-closure-billings`, {
          responseType: 'blob',
          params: {
            ...this.buildRequestParams(),
            export: 'xls'
          },
          paramsSerializer: p => qs.stringify(p, { arrayFormat: 'brackets' })
        })

        const file = new Blob([response.data], { type: 'application/vnd.ms-excel' })
        const url = URL.createObjectURL(file)
        const link = document.createElement('a')
        link.href = url
        link.download = `Facturacion_${moment().format('YYYY-MM-DD')}.xls`
        link.click()
        URL.revokeObjectURL(url)
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: 'Error al exportar la tabla a Excel',
          color: 'danger'
        })
      } finally {
        this.isExporting = false
      }
    },

    openCommentModal(comment, title = 'Comentario completo') {
      this.fullCommentText = comment
      this.commentModalTitle = title
      this.commentModalActive = true
    },

    closeCommentModal() {
      this.commentModalActive = false
      this.fullCommentText = ''
    }

  }
}
</script>
